import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAuth } from '../../contexts/AuthContext';

const MainSidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const itemRefs = useRef({});

  const toggleCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  const [expandedGroups, setExpandedGroups] = useState({
    crm: true,
    workforce: true,
    financial: true,
    compliance: true,
    hr: true,
    settings: true
  });

  const { userProfile } = useAuth(); // Get user profile for permissions

  // Map sidebar IDs to backend permission module keys
  const permissionMap = {
    'dashboard': 'dashboard',
    'company-config': 'settings', // Assuming generic settings or specific config module
    'crm': 'crm',
    'crm-lead-management': 'crm',
    'telecaller-dashboard': 'crm',
    'hr': 'hr',
    'hr-management': 'hr',
    'offer-letters': 'hr',
    'probation-letters': 'hr',
    'increment-letters': 'hr',
    'exit-letters': 'hr',
    'applicant-tracking': 'hr', // Utilizing HR permissions for ATS
    'walkin-drives': 'hr',
    'ai-interview': 'hr',
    'employee-management': 'employee_management',
    'employee-deployment': 'employee_management',
    'attendance-management': 'hr',
    'attendance-tracker': 'hr',
    'live-attendance': 'hr',
    'client-management': 'client_management',
    'client-site-management': 'client_management',
    'client-requirements-matching': 'client_management',
    'payroll-management': 'payroll',
    'invoice-billing': 'invoice',
    'compliance': 'compliance',
    'compliance-reports': 'compliance',
    'settings': 'settings'
  };

  const hasPermission = (itemId) => {
    // Super admin has access to everything
    if (userProfile?.role === 'super_admin') return true;

    // If no profile or permissions yet, hide everything (except maybe dashboard?)
    if (!userProfile?.permissions) return false;

    const moduleKey = permissionMap[itemId] || itemId;

    // Check if permission exists and has read access or 'all'
    const modulePerms = userProfile.permissions[moduleKey];
    return modulePerms && (modulePerms.all || modulePerms.read);
  };

  const filterNavigation = (items) => {
    return items.reduce((acc, item) => {
      // If item has children, filter them first
      if (item.children) {
        const filteredChildren = filterNavigation(item.children);
        // Only include parent if it has accessible children
        if (filteredChildren.length > 0) {
          acc.push({ ...item, children: filteredChildren });
        }
      }
      // If item has no children, check its own permission
      else if (hasPermission(item.id)) {
        acc.push(item);
      }
      return acc;
    }, []);
  };

  // Base navigation items
  const allNavigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/erp/dashboard-overview',
      icon: 'LayoutDashboard'
    },
    {
      id: 'company-config',
      label: 'Company Config',
      path: '/erp/company-config',
      icon: 'Settings'
    },
    {
      id: 'crm',
      label: 'CRM & Lead Management',
      icon: 'Users',
      children: [
        {
          id: 'crm-lead-management',
          label: 'Lead Management',
          path: '/erp/crm-lead-management',
          icon: 'UserPlus'
        },
        {
          id: 'telecaller-dashboard',
          label: 'Lead Calls Dashboard',
          path: '/erp/telecaller-dashboard',
          icon: 'Phone'
        }
      ]
    },
    {
      id: 'hr',
      label: 'Human Resources',
      icon: 'Briefcase',
      children: [
        {
          id: 'lifecycle-dashboard',
          label: 'Lifecycle Dashboard',
          path: '/erp/lifecycle-dashboard',
          icon: 'GitBranch'
        },
        {
          id: 'hr-management',
          label: 'HR Management',
          path: '/erp/hr/hr-management',
          icon: 'Users'
        },
        {
          id: 'increment-letters',
          label: 'Increment Letters',
          path: '/erp/hr/increment-letters',
          icon: 'TrendingUp'
        },
        {
          id: 'exit-letters',
          label: 'Exit Letters',
          path: '/erp/hr/exit-letters',
          icon: 'LogOut'
        }
      ]
    },
    {
      id: 'workforce',
      label: 'Staff & Placement',
      icon: 'UserCheck',
      children: [
        {
          id: 'applicant-tracking',
          label: 'Application Process',
          path: '/erp/applicant-tracking-system',
          icon: 'FileUser'
        },
        {
          id: 'walkin-drives',
          label: 'Walk-in Drives',
          path: '/erp/walkin-drives',
          icon: 'QrCode'
        },
        {
          id: 'ai-interview',
          label: 'AI Interview',
          path: '/erp/ai-interview',
          icon: 'Mic'
        },
        {
          id: 'offer-letters',
          label: 'Offer Letters',
          path: '/erp/applicant-tracking-system/offer-letters',
          icon: 'FileText'
        },
        {
          id: 'probation-letters',
          label: 'Probation Letters',
          path: '/erp/applicant-tracking-system/probation-letters',
          icon: 'Clock'
        },
        {
          id: 'employee-management',
          label: 'Employee Management',
          path: '/erp/employee-management',
          icon: 'Users'
        },
        {
          id: 'employee-deployment',
          label: 'Employee Deployment',
          path: '/erp/employee-deployment-management',
          icon: 'MapPin'
        },
        {
          id: 'attendance-management',
          label: 'Attendance Management',
          path: '/erp/attendance-management',
          icon: 'CalendarCheck'
        },
        {
          id: 'attendance-tracker',
          label: 'Attendance Tracker',
          path: '/erp/attendance-tracker',
          icon: 'CalendarDays'
        },
        {
          id: 'live-attendance',
          label: 'Live Attendance',
          path: '/erp/live-attendance',
          icon: 'Radio'
        }
      ]
    },
    {
      id: 'financial',
      label: 'Financial Operations',
      icon: 'Calculator',
      children: [
        {
          id: 'client-management',
          label: 'Client Management',
          path: '/erp/client-management',
          icon: 'Building2'
        },
        {
          id: 'client-site-management',
          label: 'Client Site Management',
          path: '/erp/client-site-management',
          icon: 'MapPin'
        },
        {
          id: 'client-requirements-matching',
          label: 'Client Requirements & Matching',
          path: '/erp/client-requirements-matching',
          icon: 'ClipboardList'
        },
        {
          id: 'payroll-management',
          label: 'Payroll Management',
          path: '/erp/payroll-management',
          icon: 'Banknote'
        },
        {
          id: 'invoice-billing',
          label: 'Client Billing',
          path: '/erp/invoice-billing-management',
          icon: 'Receipt'
        }
      ]
    },
    {
      id: 'compliance',
      label: 'Regulatory Oversight',
      icon: 'Shield',
      children: [
        {
          id: 'compliance-reports',
          label: 'Compliance & Reports',
          path: '/erp/compliance-statutory-reports',
          icon: 'FileText'
        }
      ]
    },
    {
      id: 'settings',
      label: 'Settings',
      path: '/erp/settings',
      icon: 'Settings'
    }
  ];

  const navigationItems = filterNavigation(allNavigationItems);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const isActiveItem = (path) => {
    return location.pathname === path;
  };

  const isActiveGroup = (children) => {
    return children?.some(child => location.pathname === child.path);
  };

  const findActiveItem = (items, path) => {
    for (const item of items) {
      if (item.path === path) {
        return { id: item.id };
      }
      if (item.children) {
        const childMatch = item.children.find(child => child.path === path);
        if (childMatch) {
          return { id: childMatch.id, parentId: item.id };
        }
      }
    }
    return null;
  };

  useEffect(() => {
    const active = findActiveItem(navigationItems, location.pathname);
    if (!active) return;

    if (active.parentId) {
      setExpandedGroups(prev => (
        prev[active.parentId] ? prev : { ...prev, [active.parentId]: true }
      ));
    }

    const target = itemRefs.current[active.id] || (active.parentId && itemRefs.current[active.parentId]);
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ block: 'nearest' });
    }
  }, [location.pathname]);

  const renderNavigationItem = (item, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedGroups[item.id];
    const isActive = hasChildren ? isActiveGroup(item.children) : isActiveItem(item.path);

    if (hasChildren) {
      // Collapsed view - show icon only with hover popover
      if (isCollapsed) {
        return (
          <div
            key={item.id}
            ref={(el) => { if (el) itemRefs.current[item.id] = el; }}
            className="mb-1 relative group"
          >
            <Button
              variant="ghost"
              size="icon"
              className={`w-full h-10 ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              title={item.label}
            >
              <Icon name={item.icon} size={18} />
            </Button>

            {/* Popover on hover when collapsed - positioned outside sidebar */}
            <div className="absolute left-full ml-2 top-0 z-[100] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
              <div className="bg-card border border-border rounded-lg shadow-2xl p-3 min-w-[220px] max-w-[300px]">
                <p className="font-semibold text-foreground text-sm mb-2 pb-2 border-b border-border">{item.label}</p>
                <div className="space-y-1">
                  {item.children.map(child => {
                    const isChildActive = location.pathname === child.path;
                    return (
                      <Link
                        key={child.id}
                        to={child.path}
                        onClick={onClose}
                        title={child.label}
                        className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-md transition-colors ${isChildActive
                          ? 'bg-primary text-primary-foreground font-semibold'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                      >
                        <Icon name={child.icon} size={14} />
                        <span className="truncate">{child.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Expanded view
      return (
        <div
          key={item.id}
          ref={(el) => { if (el) itemRefs.current[item.id] = el; }}
          className="mb-0.5"
        >
          <Button
            variant="ghost"
            onClick={() => toggleGroup(item.id)}
            title={item.label}
            className={`w-full justify-start px-2.5 py-2 h-auto min-w-0 ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
          >
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <Icon name={item.icon} size={18} className="shrink-0" />
              <div className="flex-1 text-left min-w-0">
                <p className="text-xs font-semibold leading-snug truncate text-left" title={item.label}>{item.label}</p>
              </div>
              <Icon
                name="ChevronDown"
                size={15}
                className={`shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                  }`}
              />
            </div>
          </Button>

          {isExpanded && (
            <div className="ml-2.5 mt-0.5 space-y-0.5 border-l border-border pl-2">
              {item.children.map(child => renderNavigationItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    // Single item (no children)
    if (isCollapsed) {
      return (
        <Link
          key={item.id}
          to={item.path}
          onClick={onClose}
          ref={(el) => { if (el) itemRefs.current[item.id] = el; }}
        >
          <Button
            variant="ghost"
            size="icon"
            className={`w-full h-10 mb-1 ${isActive
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            title={item.label}
          >
            <Icon name={item.icon} size={18} />
          </Button>
        </Link>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path}
        onClick={onClose}
        title={item.label}
        ref={(el) => { if (el) itemRefs.current[item.id] = el; }}
      >
        <Button
          variant="ghost"
          className={`w-full justify-start px-2.5 py-2 h-auto mb-1 min-w-0 ${isActive
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Icon name={item.icon} size={18} className="shrink-0" />
            <div className="text-left min-w-0">
              <p className="text-xs font-semibold leading-snug truncate text-left" title={item.label}>{item.label}</p>
            </div>
          </div>
        </Button>
      </Link>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Mobile responsive */}
      <aside className={`
        fixed left-0 bg-card border-r border-border
        transform transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        ${isCollapsed ? 'w-16 lg:w-16' : 'w-72 lg:w-72'}
        top-16 bottom-0
        z-50
        overflow-hidden
        shadow-sm
        m-0
      `} style={{ marginTop: 0, top: '4rem' }}>
        <div className="flex flex-col h-full overflow-hidden">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              {!isCollapsed && (
                <div>
                  <h2 className="text-base font-semibold text-foreground">Navigation</h2>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleCollapse}
                  className="hidden lg:flex"
                  title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                >
                  <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="lg:hidden"
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>
          </div>

          {/* Navigation Items - Scrollable */}
          <nav className="flex-1 p-2 overflow-y-auto overflow-x-hidden">
            <div className="space-y-0.5">
              {navigationItems.map(item => renderNavigationItem(item))}
            </div>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default MainSidebar;